/**
 * AI providers — workspace AI policy (ADR-0020), Settings → Security #ai-providers.
 * V101-5-4: the remediation coach's "No AI provider" state links here (with ?return=<finding url>).
 *
 * GET /ai-policy any member (read-only view + AskAdmin to the owner);
 * PUT /ai-policy owner only (a data-boundary decision). 422 { error } is shown inline, tied to the list.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Field, Select, Spinner } from '@aigovops/design-system';
import { getAiPolicy, putAiPolicy, type AiPolicy, type AiProviderOffer } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { errorText } from './today/shared';
import { AskAdmin } from './AskAdmin';
import { allowLabel, firstCapable } from './RemediationDraft';
import './ai-provider-settings.css';

const BOUNDARY: Record<string, string> = {
  in_boundary: 'In boundary', on_prem: 'On-prem', vendor_us: 'Vendor · US', vendor_eu: 'Vendor · EU', vendor_global: 'Vendor · global',
};
export const boundaryLabel = (b: string) => BOUNDARY[b] ?? b.replace(/_/g, ' ');
const names = (ps: AiProviderOffer[]) => ps.map((p) => p.display_name).join(', ');

export function AiProviderSettings({ slug, role }: { slug: string; role: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: queryKeys.aiPolicy(slug), queryFn: () => getAiPolicy(slug), retry: false, staleTime: 60_000 });
  const owner = role === 'owner';
  const [sp] = useSearchParams();
  const ret = sp.get('return');
  // Same-workspace paths only (never an open redirect).
  const returnTo = ret && ret.startsWith(`/w/${slug}/`) && !ret.startsWith('//') ? ret : null;
  const [allowed, setAllowed] = useState<string[]>([]);
  const [def, setDef] = useState('');
  const [inBoundary, setInBoundary] = useState(false);
  const removed = useRef<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [msg, setMsg] = useState('');
  const load = (p: AiPolicy) => { setAllowed(p.allowed_providers); setDef(p.default_provider_id); setInBoundary(p.federal_boundary_only); removed.current = []; };
  useEffect(() => { if (q.data) load(q.data.policy); }, [q.data]);
  const providers = q.data?.providers ?? [];
  const dirty = !!q.data && (def !== q.data.policy.default_provider_id || inBoundary !== q.data.policy.federal_boundary_only
    || [...allowed].sort().join() !== [...q.data.policy.allowed_providers].sort().join());
  const canDraft = useMemo(() => providers.some((p) => p.can_draft && allowed.includes(p.provider_id)), [providers, allowed]);
  const capable = providers.filter((p) => p.can_draft);
  const offLimits = (p: AiProviderOffer) => inBoundary && p.boundary !== 'in_boundary';
  const say = (m: string) => { setMsg(''); window.setTimeout(() => setMsg(m), 40); };
  const changed = () => { setSaved(false); setErr(null); setConfirmOff(false); };

  const toggle = (p: AiProviderOffer, on: boolean) => {
    if (offLimits(p)) return;
    changed();
    const next = on ? [...new Set([...allowed, p.provider_id])] : allowed.filter((x) => x !== p.provider_id);
    setAllowed(next);
    if (!next.includes(def)) setDef(next[0] ?? '');
  };
  // M2: in-boundary only removes the others and remembers them; unticking before Save restores them.
  const setBoundaryOnly = (on: boolean) => {
    changed();
    setInBoundary(on);
    if (on) {
      const out = allowed.filter((id) => providers.find((p) => p.provider_id === id)?.boundary !== 'in_boundary');
      removed.current = out;
      const keep = allowed.filter((id) => !out.includes(id));
      setAllowed(keep);
      if (!keep.includes(def)) setDef(keep[0] ?? '');
      say(out.length ? `Removed ${names(providers.filter((p) => out.includes(p.provider_id)))} (not in boundary)` : 'In-boundary only');
    } else {
      const back = removed.current;
      removed.current = [];
      if (back.length) { setAllowed((a) => [...new Set([...a, ...back])]); say(`Restored ${names(providers.filter((p) => back.includes(p.provider_id)))}`); }
      else say('Any boundary');
    }
  };
  const put = async (body: Omit<AiPolicy, 'updated_at'>) => {
    setBusy(true); setErr(null); setSaved(false);
    try {
      const r = await putAiPolicy(slug, body);
      qc.setQueryData(queryKeys.aiPolicy(slug), { policy: r.policy, providers: r.providers });
      setSaved(true); setConfirmOff(false);
      say('Saved');
    } catch (e) { setErr(errorText(e, 'That did not save.')); }
    finally { setBusy(false); }
  };
  const save = () => {
    if (busy || !dirty) return;
    if (!allowed.length) { setErr('Allow at least one provider.'); return; }
    // M2: turning drafted fixes off takes a second, explicit click.
    if (!canDraft && !confirmOff) { setConfirmOff(true); say('No allowed provider can draft fixes. Select Save again to confirm.'); return; }
    void put({ allowed_providers: allowed, default_provider_id: def || allowed[0]!, federal_boundary_only: inBoundary });
  };
  const allowOne = (p: AiProviderOffer) => {
    if (busy || !q.data) return;
    const base = q.data.policy;
    void put({ allowed_providers: [...new Set([...base.allowed_providers, p.provider_id])], default_provider_id: base.default_provider_id, federal_boundary_only: base.federal_boundary_only });
  };
  const candidate = q.data ? firstCapable(providers, { ...q.data.policy, allowed_providers: allowed, federal_boundary_only: inBoundary }) : null;

  return (
    <div id="ai-providers" className="aip" tabIndex={-1} role="region" aria-labelledby="ai-providers-h">
      <Card>
        <div className="aip-body">
          <h2 id="ai-providers-h" className="aip-h">AI providers</h2>
          {q.isLoading ? <Spinner label="Loading AI providers" /> : q.error || !q.data ? (
            <Alert variant="error"><p>AI providers could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
          ) : (
            <>
              {!canDraft ? (
                <Alert variant="warning">
                  <p>{capable.length ? `No allowed provider can draft fixes. ${names(capable)} can.` : 'No provider here can draft fixes.'}</p>
                  {owner && candidate ? <Button variant="secondary" compact aria-disabled={busy || undefined} onClick={() => allowOne(candidate)}>{allowLabel(candidate)}</Button> : null}
                </Alert>
              ) : null}
              {owner ? (
                <>
                  <div className="aip-toggle">
                    <label className="aip-row">
                      <input type="checkbox" checked={inBoundary} aria-describedby="aip-boundary-hint" onChange={(e) => setBoundaryOnly(e.currentTarget.checked)} />
                      <span className="aip-name">In-boundary only</span>
                    </label>
                    <span id="aip-boundary-hint" className="aip-hint">Only providers that keep data inside your boundary</span>
                  </div>
                  <fieldset className="aip-list" aria-describedby={err ? 'aip-err' : undefined} aria-invalid={err ? true : undefined}>
                    <legend className="aip-legend">Allowed providers</legend>
                    {providers.map((p) => {
                      const off = offLimits(p);
                      return (
                        <label key={p.provider_id} className="aip-row" data-off={off || undefined}>
                          <input type="checkbox" checked={allowed.includes(p.provider_id)} aria-disabled={off || undefined}
                            aria-describedby={off ? `aip-off-${p.provider_id}` : undefined}
                            onChange={(e) => { if (!off) toggle(p, e.currentTarget.checked); }} />
                          <span className="aip-name">{p.display_name}</span>
                          <Badge>{boundaryLabel(p.boundary)}</Badge>
                          {p.can_draft ? <Badge>Can draft fixes</Badge> : null}
                          {off ? <span id={`aip-off-${p.provider_id}`} className="aip-dim">Not in boundary</span> : null}
                        </label>
                      );
                    })}
                  </fieldset>
                  <Field id="aip-default" label="Default">
                    <Select value={def} onChange={(e) => { changed(); setDef(e.currentTarget.value); }} disabled={!allowed.length}>
                      {providers.filter((p) => allowed.includes(p.provider_id)).map((p) => <option key={p.provider_id} value={p.provider_id}>{p.display_name}</option>)}
                    </Select>
                  </Field>
                  {err ? <p id="aip-err" className="aip-err" role="alert">{err}</p> : null}
                  <div className="aip-actions">
                    <Button variant={confirmOff ? 'secondary' : 'primary'} compact aria-disabled={busy || !dirty || undefined}
                      aria-describedby={!dirty ? 'aip-nochange' : undefined} onClick={save}>
                      {busy ? 'Saving…' : confirmOff ? 'Save, turn off drafted fixes' : 'Save'}
                    </Button>
                    {!dirty && !saved ? <span id="aip-nochange" className="aip-dim">No changes</span> : null}
                    {saved ? <span className="aip-dim">{returnTo ? <>Saved · <Link to={returnTo}>Back to the finding</Link></> : 'Saved'}</span> : null}
                  </div>
                </>
              ) : (
                <>
                  <ul className="aip-ro" aria-label="AI providers">
                    {providers.map((p) => {
                      const on = q.data!.policy.allowed_providers.includes(p.provider_id);
                      return (
                        <li key={p.provider_id} className="aip-ro-row">
                          <Badge tone={on ? 'accent' : 'neutral'}>{on ? (p.provider_id === q.data!.policy.default_provider_id ? 'Default' : 'Allowed') : 'Not allowed'}</Badge>
                          <span className="aip-name">{p.display_name}</span>
                          <Badge>{boundaryLabel(p.boundary)}</Badge>
                          <span>{p.can_draft ? <Badge>Can draft fixes</Badge> : null}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <AskAdmin slug={slug} single roles={['owner']} lead="A workspace owner" what="changes AI providers" subject="Change the workspace AI providers" />
                </>
              )}
              <p role="status" className="sr-only">{msg}</p>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
